/**
 * daddl-cart.js
 * -----------------------------------------------------------------------------
 * Custom, app-free enhancements for the Dawn cart drawer:
 *   1. <daddl-cart-rewards>  – a milestone / reward progress bar
 *                              (free shipping, discount tiers, …).
 *   2. <daddl-cart-upsells>  – a "recommended products" grid with one-tap
 *                              add-to-cart, wired into Dawn's section rendering.
 *   3. <daddl-cart-timer>    – a session-persistent "items reserved" countdown.
 *   4. <daddl-cart-club-earn> – Club store-credit estimate (rate × discounted total).
 *   5. CartItems patches     – qty 0 removes the line; lock +/−/remove while a
 *                              request is in flight (not in frozen Dawn cart.js).
 *
 * Design notes for future developers
 * ----------------------------------
 * - These are self-contained Web Components. Dawn re-renders the cart drawer
 *   HTML on every cart mutation (see `cart.js` / `cart-drawer.js`), which
 *   destroys and recreates our elements. Because the logic lives in
 *   `connectedCallback` / `disconnectedCallback`, each component transparently
 *   re-initialises after any re-render — no manual re-binding required.
 * - The reward bar's fill is ALSO rendered server-side (inline width) by
 *   snippets/daddl-cart-rewards.liquid, so it is correct even before/without JS
 *   and on every server re-render. The JS here keeps it live between renders.
 * - Configuration is passed from Liquid via `data-*` attributes so the same
 *   compiled asset works for any shop/locale/market without edits.
 * - No external dependencies; safe to load once (guarded custom-element
 *   registration).
 * -----------------------------------------------------------------------------
 */

(function () {
  'use strict';

  /**
   * @typedef {Object} ShopifyCartItem
   * @property {number} id           Variant id.
   * @property {number} product_id   Parent product id.
   * @property {number} quantity     Quantity in cart.
   * @property {Object} properties   Line-item properties.
   *
   * @typedef {Object} ShopifyCart
   * @property {number} total_price          Cart total in the presentment currency's minor units.
   * @property {number} item_count           Total number of items.
   * @property {string} [currency]           Presentment currency ISO code (e.g. "AED", "EUR").
   * @property {ShopifyCartItem[]} items     Line items.
   */

  /* ---------------------------------------------------------------------------
   * Shared utilities
   * ------------------------------------------------------------------------- */

  /**
   * Subscribe to Dawn's cart-update pub/sub event, resiliently.
   * @param {(cart: ShopifyCart | null) => void} callback Invoked with cart data (or null when unknown).
   * @returns {() => void} Unsubscribe function.
   */
  function onCartUpdate(callback) {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      return subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        var cart = event && event.cartData && typeof event.cartData.total_price === 'number' ? event.cartData : null;
        callback(cart);
      });
    }
    var handler = function () {
      callback(null);
    };
    document.addEventListener('daddl:cart:refresh', handler);
    return function () {
      document.removeEventListener('daddl:cart:refresh', handler);
    };
  }

  /**
   * Resolve cart data, preferring an event payload and falling back to /cart.js.
   * @param {ShopifyCart | null} maybeCart Cart data from an event, if available.
   * @returns {Promise<ShopifyCart | null>}
   */
  function resolveCart(maybeCart) {
    if (maybeCart && typeof maybeCart.total_price === 'number') {
      return Promise.resolve(maybeCart);
    }
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  /**
   * The amount reward progress is measured against: the subtotal AFTER
   * line-level discounts but BEFORE cart-level discounts. Using total_price
   * would make an unlocked "10% off" tier lower the measured amount and drag
   * the bar back below the milestone that triggered it.
   * @param {ShopifyCart} cart
   * @returns {number} Amount in minor units.
   */
  function progressAmount(cart) {
    return typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price : cart.total_price;
  }

  /**
   * How many units in the cart are 100%-discounted gifts ("buy X get Y free").
   * Mirrors snippets/daddl-cart-free-units.liquid so the server-rendered bar
   * and the live one can never disagree.
   * @param {ShopifyCart} cart
   * @returns {number}
   */
  function freeUnitCount(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce(function (total, item) {
      var allocations = item.line_level_discount_allocations || item.discount_allocations || [];
      var unitPrice = item.original_price || item.price || 0;
      if (!unitPrice || !allocations.length) return total;

      var savings = allocations.reduce(function (sum, allocation) {
        var application = allocation.discount_application || {};
        var value = parseFloat(application.value);
        if (application.value_type === 'percentage' && value >= 100) {
          return sum + (allocation.amount || 0);
        }
        return sum;
      }, 0);

      if (savings <= 0) return total;
      return total + Math.min(Math.floor(savings / unitPrice), item.quantity || 0);
    }, 0);
  }

  /**
   * Item count excluding free gift units. Claiming a free bag must not push
   * the shopper toward the next "buy X get Y" milestone.
   * @param {ShopifyCart} cart
   * @returns {number}
   */
  function paidItemCount(cart) {
    var count = typeof cart.item_count === 'number' ? cart.item_count : 0;
    return Math.max(0, count - freeUnitCount(cart));
  }

  /* Shared so every surface measures milestone progress the same way — the
   * PDP tier bars (snippets/daddl-spending-tiers.liquid) included. One rule,
   * one place; the Liquid equivalents are daddl-cart-line-free-units and
   * daddl-cart-free-units. */
  window.daddlFreeUnitCount = freeUnitCount;
  window.daddlPaidItemCount = paidItemCount;

  /**
   * Format a minor-unit amount (e.g. 1500) into a human string ("15 AED").
   * @param {number} minorUnits Amount in the shop's smallest currency unit.
   * @param {string} currency   Currency label appended to the value.
   * @returns {string}
   */
  function formatAmount(minorUnits, currency) {
    var major = minorUnits / 100;
    var value = Number.isInteger(major) ? String(major) : major.toFixed(2);
    return currency ? value + ' ' + currency : value;
  }

  /**
   * Shopify money_format → string. Used by the Club earn line so the amount
   * matches Liquid `| money` after Ajax cart updates.
   * @param {number} cents Minor units.
   * @param {string} format shop.money_format
   * @returns {string}
   */
  function formatMoneyFromFormat(cents, format) {
    var value = (Math.max(0, cents) / 100).toFixed(2);
    var whole = value.split('.')[0];
    var template = format || '{{amount}}';
    return template
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
      .replace(/\{\{\s*amount\s*\}\}/g, value);
  }

  /**
   * Leading Font Awesome icon for the rewards message (FA 6 is loaded
   * globally in theme.liquid).
   * @param {string} name Icon name without the fa- prefix (e.g. "gift").
   * @returns {string} Icon HTML.
   */
  function rewardsMsgIcon(name) {
    return '<i class="fa-solid fa-' + name + ' daddl-cart-rewards__msg-icon" aria-hidden="true"></i>';
  }

  /**
   * Record which DADDL module put something in the cart, as a CART ATTRIBUTE.
   *
   * Attribution must NEVER be a line-item property: Shopify keys cart lines by
   * variant + properties, so a bag added from the upsell / video / slots /
   * pick-and-mix would sit on its own row next to the identical bag added from
   * the PDP. Two rows of the same product is a bug to shoppers, and it also
   * scatters "Buy X get Y" free units across duplicate lines. The bundle
   * builder already used this attribute pattern; everything else follows it.
   *
   * Fire-and-forget: attributes do not affect rendering, so this never blocks
   * the add or the drawer re-render. The value is cart-wide, so a later add
   * overwrites an earlier one — that is the trade for a single clean row.
   * @param {string} name Attribute key, e.g. "daddl_cart_upsell".
   * @param {string} [value] Optional detail (e.g. a video tracking label).
   */
  function tagCartSource(name, value) {
    var url = (window.routes && window.routes.cart_update_url) || '/cart/update';
    var attributes = {};
    attributes[name] = value || 'true';

    fetch(url + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ attributes: attributes }),
    }).catch(function (error) {
      console.error('[daddl-cart] attribution failed', error);
    });
  }

  /* Shared with the sections that add to cart from outside this file
   * (video trust, slots, pick and mix). */
  window.daddlTagCartSource = tagCartSource;

  /**
   * Register a custom element only once (safe against duplicate script loads).
   * @param {string} tag Custom element tag name.
   * @param {CustomElementConstructor} ctor Element class.
   */
  function defineElement(tag, ctor) {
    if (!customElements.get(tag)) {
      customElements.define(tag, ctor);
    }
  }

  /* ---------------------------------------------------------------------------
   * Confetti — celebratory burst when a reward tier is unlocked
   *
   * Uses the canvas-confetti library with the same brand palette as the
   * Pick & Mix section, so celebrations feel identical across the store.
   * The library is loaded lazily (on first preload/burst), never blocking
   * page load.
   * ------------------------------------------------------------------------- */

  var CONFETTI_SRC = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
  var CONFETTI_COLORS = ['#810057', '#CFA4EC', '#E8F685', '#A64B85', '#F9EDE1', '#810057'];

  /** @type {Promise<Function | null> | null} */
  var confettiLibPromise = null;

  /**
   * Load canvas-confetti once (reuses the global if e.g. Pick & Mix already
   * loaded it). Resolves with the confetti function, or null on failure.
   * @returns {Promise<Function | null>}
   */
  function loadConfettiLib() {
    if (typeof window.confetti === 'function') return Promise.resolve(window.confetti);
    if (!confettiLibPromise) {
      confettiLibPromise = new Promise(function (resolve) {
        var script = document.createElement('script');
        script.src = CONFETTI_SRC;
        script.async = true;
        script.onload = function () {
          resolve(typeof window.confetti === 'function' ? window.confetti : null);
        };
        script.onerror = function () {
          resolve(null);
        };
        document.head.appendChild(script);
      });
    }
    return confettiLibPromise;
  }

  /**
   * Fire a full, layered confetti celebration centred on an element (e.g. the
   * milestone badge that was just unlocked). Several overlapping shots with
   * different spreads/velocities read as one rich explosion, followed by a
   * short drizzle so it doesn't cut off abruptly.
   * @param {HTMLElement | null} element Origin element; falls back to the
   *   upper middle of the viewport when missing or not visible.
   */
  function confettiBurstFrom(element) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    loadConfettiLib().then(function (confettiFn) {
      if (typeof confettiFn !== 'function') return;

      // Normalised origin, measured at fire time (the element may have been
      // re-rendered away by a drawer refresh).
      var origin = { x: 0.5, y: 0.3 };
      if (element) {
        var rect = element.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          origin = {
            x: (rect.left + rect.width / 2) / window.innerWidth,
            y: (rect.top + rect.height / 2) / window.innerHeight,
          };
        }
      }

      var TOTAL_PARTICLES = 320;

      /**
       * @param {number} ratio Share of TOTAL_PARTICLES for this shot.
       * @param {Object} options canvas-confetti overrides for this shot.
       */
      function fire(ratio, options) {
        confettiFn(
          Object.assign(
            {
              origin: origin,
              colors: CONFETTI_COLORS,
              zIndex: 2147483646,
              disableForReducedMotion: true,
              particleCount: Math.floor(TOTAL_PARTICLES * ratio),
            },
            options
          )
        );
      }

      // The canonical layered recipe: tight+fast core, wide+slow halo.
      fire(0.25, { spread: 26, startVelocity: 55 });
      fire(0.2, { spread: 60 });
      fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
      fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
      fire(0.1, { spread: 120, startVelocity: 45 });

      // Trailing drizzle (~0.8s) so the burst fades out instead of stopping.
      var end = Date.now() + 800;
      (function frame() {
        confettiFn({
          particleCount: 2,
          spread: 80,
          startVelocity: 30,
          ticks: 120,
          origin: origin,
          colors: CONFETTI_COLORS,
          zIndex: 2147483646,
          disableForReducedMotion: true,
        });
        if (Date.now() < end) window.requestAnimationFrame(frame);
      })();
    });
  }

  /* Shared with other DADDL components (e.g. the bundle builder modal on the
   * product page) so tier celebrations look identical everywhere. The preload
   * lets the modal warm the library up on open, so the first unlock fires
   * without a network delay. */
  window.daddlConfettiBurst = confettiBurstFrom;
  window.daddlConfettiPreload = loadConfettiLib;

  /**
   * Thresholds (minor units) known to be reached, shared across drawer
   * re-renders (the module survives even though the component is recreated on
   * every cart mutation). `null` until the first paint so confetti never fires
   * for tiers that were already unlocked when the page loaded.
   * @type {Set<number> | null}
   */
  var confettiBaseline = null;

  /**
   * Last painted fill widths (%) of the rewards bar segments, shared across
   * drawer re-renders. Dawn replaces the drawer HTML on every cart change, so
   * the fill elements are recreated already AT their new widths and the CSS
   * width transition never fires. Remembering the previous widths lets the
   * new elements start there and animate smoothly to the new values.
   * @type {number[] | null}
   */
  var lastFillPercents = null;

  /* ---------------------------------------------------------------------------
   * <daddl-cart-rewards> — milestone / reward progress bar
   * ------------------------------------------------------------------------- */

  /**
   * Renders a segmented progress bar: one segment per milestone, each with
   * its own fill, unlocking when the progress reaches the segment's earned
   * amount (`data-threshold` minus `data-free`). Fills are computed
   * identically to the server-side Liquid so the two never disagree.
   */
  class DaddlCartRewards extends HTMLElement {
    connectedCallback() {
      /** @type {HTMLElement | null} */
      this.messageEl = this.querySelector('[data-daddl-rewards-message]');

      /* 'amount' measures minor currency units, 'items' counts cart items
       * (quantity offers like "buy 8 bags get 1 free"). Thresholds emitted by
       * Liquid are already in the matching unit. */
      this.mode = this.dataset.mode || 'amount';
      /* Free gift units already in the cart. A milestone with `free > 0` is
       * only finished once these have been claimed. */
      this.freeUnits = parseInt(this.dataset.freeUnits, 10) || 0;
      this.currency = this.dataset.currency || '';
      this.strAway = this.dataset.strAway || '';
      this.strFreeEarned = this.dataset.strFreeEarned || '';
      this.strComplete = this.dataset.strComplete || '';
      this.strBagOne = this.dataset.strBagOne || 'bag';
      this.strBagOther = this.dataset.strBagOther || 'bags';

      var nodes = Array.prototype.slice.call(this.querySelectorAll('[data-threshold]'));
      nodes.sort((a, b) => (parseInt(a.dataset.threshold, 10) || 0) - (parseInt(b.dataset.threshold, 10) || 0));

      /** @type {{ el: HTMLElement, fillEl: HTMLElement | null, threshold: number, free: number, earnAt: number, label: string }[]} */
      this.milestones = nodes.map((el) => {
        var threshold = parseInt(el.dataset.threshold, 10) || 0;
        var free = parseInt(el.dataset.free, 10) || 0;
        return {
          el: el,
          fillEl: el.querySelector('[data-daddl-rewards-seg-fill]'),
          threshold: threshold,
          free: free,
          // The tier is visually EARNED once the paid amount is in the cart;
          // the gap up to `threshold` is the free item(s) the customer picks.
          // (free is always 0 in amount mode, so earnAt === threshold there.)
          earnAt: threshold - free,
          label: el.dataset.label || '',
        };
      });

      this.update(parseInt(this.dataset.total, 10) || 0);
      this.refresh();

      this.unsubscribe = onCartUpdate((cart) => {
        if (cart) {
          this.applyCart(cart);
        } else {
          this.refresh();
        }
      });
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    /** Pull the live cart and repaint (source of truth). */
    refresh() {
      resolveCart(null).then((cart) => {
        if (cart) this.applyCart(cart);
      });
    }

    /**
     * Apply a resolved cart: keep the currency market-accurate, then repaint.
     * In items mode a payload without item_count (e.g. an /cart/add.js
     * response) can't be trusted – re-fetch the real cart instead.
     * @param {ShopifyCart} cart
     */
    applyCart(cart) {
      if (this.mode === 'items') {
        // A payload without items (e.g. an /cart/add.js response) can't be
        // trusted for the paid/free split – re-fetch the real cart instead.
        if (typeof cart.item_count === 'number' && Array.isArray(cart.items)) {
          this.freeUnits = freeUnitCount(cart);
          this.update(paidItemCount(cart));
        } else {
          this.refresh();
        }
        return;
      }
      if (cart.currency) this.currency = cart.currency;
      this.update(progressAmount(cart));
    }

    /**
     * Recompute segment fills, node states, and the status message for a
     * cart total. Each segment fills over the range between the previous
     * tier's earned amount and its own.
     * @param {number} total Cart progress in minor units (or item count).
     */
    update(total) {
      var newPercents = [];
      var prevEarn = 0;
      this.milestones.forEach((milestone, index) => {
        var span = Math.max(milestone.earnAt - prevEarn, 1);
        var pct = Math.max(0, Math.min(100, ((total - prevEarn) / span) * 100));
        prevEarn = milestone.earnAt;
        newPercents.push(pct);

        if (!milestone.fillEl) return;

        // Smooth animation across drawer re-renders: the recreated element is
        // already at its new width, so snap it back to the previous width
        // (transition off), force a reflow, then let the CSS transition carry
        // it to the new value.
        var last = lastFillPercents && lastFillPercents[index];
        if (typeof last === 'number' && Math.abs(last - pct) > 0.1) {
          milestone.fillEl.style.transition = 'none';
          milestone.fillEl.style.width = last + '%';
          void milestone.fillEl.offsetWidth;
          milestone.fillEl.style.transition = '';
        }

        milestone.fillEl.style.width = pct + '%';
      });
      lastFillPercents = newPercents;

      /** @type {{ earnAt: number, free: number, unclaimed: number, label: string } | null} */
      var nextReward = null;
      var reachedNow = new Set();

      this.milestones.forEach((milestone) => {
        // Earned (checked, filled, celebrated) as soon as the PAID amount is
        // there; the free item(s) on top are claimed, not paid for.
        var reached = total >= milestone.earnAt;
        milestone.el.classList.toggle('is-reached', reached);
        milestone.el.setAttribute('aria-disabled', String(!reached));
        if (reached) reachedNow.add(milestone.threshold);

        // A gift milestone is only finished once its free unit(s) are
        // actually in the cart, so the "claim your free bag" prompt survives
        // until the shopper takes it — and disappears the moment they do.
        var unclaimed = Math.max(0, milestone.free - this.freeUnits);
        if (!nextReward && (!reached || unclaimed > 0)) {
          nextReward = {
            earnAt: milestone.earnAt,
            free: milestone.free,
            unclaimed: unclaimed,
            label: milestone.label,
          };
        }
      });

      this.celebrateNewTiers(reachedNow);

      if (!this.messageEl) return;

      if (nextReward) {
        if (this.mode === 'items' && nextReward.unclaimed > 0 && total >= nextReward.earnAt) {
          this.messageEl.innerHTML =
            rewardsMsgIcon('gift') +
            this.strFreeEarned
              .replace('__REWARD__', '<strong>' + nextReward.label + '</strong>')
              .replace('__COUNT__', '<strong>' + this.formatBags(nextReward.unclaimed) + '</strong>');
        } else {
          // Both modes count down to the PAID amount (`free` is always 0 in
          // amount mode, so earnAt is the threshold there).
          var gap = Math.max(0, nextReward.earnAt - total);
          var remaining = this.mode === 'items' ? this.formatBags(gap) : formatAmount(gap, this.currency);
          this.messageEl.innerHTML = this.strAway
            .replace('__AMOUNT__', '<strong>' + remaining + '</strong>')
            .replace('__REWARD__', '<strong>' + nextReward.label + '</strong>');
        }
      } else {
        // strComplete comes from Liquid's `t` filter, which already
        // HTML-escapes; innerHTML decodes those entities correctly.
        this.messageEl.innerHTML = rewardsMsgIcon('trophy') + this.strComplete;
      }
    }

    /**
     * Format a remaining item count ("3 bags" / "1 bag") for items mode.
     * @param {number} count
     * @returns {string}
     */
    formatBags(count) {
      return count + ' ' + (count === 1 ? this.strBagOne : this.strBagOther);
    }

    /**
     * Fire a confetti burst from each milestone that JUST became reached.
     * The very first paint only records the baseline (no confetti for tiers
     * that were already unlocked when the page loaded / drawer re-rendered).
     * @param {Set<number>} reachedNow Thresholds currently reached.
     */
    celebrateNewTiers(reachedNow) {
      if (confettiBaseline !== null) {
        var newlyReached = this.milestones.filter(
          (milestone) => reachedNow.has(milestone.threshold) && !confettiBaseline.has(milestone.threshold)
        );

        newlyReached.forEach((milestone, index) => {
          var badge = milestone.el.querySelector('.daddl-cart-rewards__badge');
          // Small delay so the drawer's open/re-render transition settles first
          // (the badge position is measured when the burst actually fires);
          // stagger when one add crosses several tiers at once.
          window.setTimeout(function () {
            confettiBurstFrom(badge);
          }, 350 + index * 300);
        });
      }
      confettiBaseline = reachedNow;
    }
  }

  /* ---------------------------------------------------------------------------
   * <daddl-cart-upsells> — recommended products with add-to-cart
   * ------------------------------------------------------------------------- */

  /**
   * Renders a grid of upsell products. Adding uses Shopify's AJAX cart API and
   * Dawn's section-rendering contract so the drawer refreshes exactly like a
   * native add. Cards for products already in the cart are hidden.
   */
  class DaddlCartUpsells extends HTMLElement {
    connectedCallback() {
      this.strError = this.dataset.strError || 'Try again';

      this.onClick = this.handleClick.bind(this);
      this.addEventListener('click', this.onClick);

      // Initial visibility is already correct (rendered server-side). Only react
      // to live cart changes that don't trigger a full drawer re-render.
      this.unsubscribe = onCartUpdate((cart) => {
        resolveCart(cart).then((resolved) => this.syncVisibility(resolved));
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.unsubscribe) this.unsubscribe();
    }

    /**
     * Hide upsell cards whose product is already in the cart; collapse the
     * component when nothing is left to recommend.
     * @param {ShopifyCart | null} cart
     */
    syncVisibility(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      var inCart = new Set(cart.items.map((item) => String(item.product_id)));
      var cards = this.querySelectorAll('[data-product-id]');
      var visible = 0;
      cards.forEach((card) => {
        var hidden = inCart.has(card.dataset.productId);
        card.hidden = hidden;
        if (!hidden) visible += 1;
      });
      this.classList.toggle('is-empty', visible === 0);
    }

    /**
     * Handle add-to-cart clicks via delegation.
     * @param {MouseEvent} event
     */
    handleClick(event) {
      var button = event.target.closest('[data-daddl-upsell-add]');
      if (!button) return;
      event.preventDefault();
      this.addVariant(button);
    }

    /**
     * Fallback drawer refresh: Dawn's own section re-fetch on cart-drawer-items.
     * Used when the add succeeded but the inline `sections` payload was
     * missing/unusable (Shopify occasionally returns null for a section).
     * @returns {boolean} Whether a refresh could be triggered.
     */
    refreshDrawer() {
      var drawerItems = document.querySelector('cart-drawer-items');
      if (drawerItems && typeof drawerItems.onCartUpdate === 'function') {
        drawerItems.onCartUpdate();
        return true;
      }
      return false;
    }

    /**
     * Show a transient, readable error in Dawn's alert region (instead of
     * stuffing the long message inside the small round add button).
     * @param {string} message
     */
    showError(message) {
      var region = document.getElementById('CartDrawer-CartErrors');
      if (!region) return;
      region.textContent = message;
      window.clearTimeout(this.errorTimeout);
      this.errorTimeout = window.setTimeout(function () {
        region.textContent = '';
      }, 4000);
    }

    /**
     * Add a single variant to the cart and refresh the drawer natively.
     *
     * Robustness notes: only a failed /cart/add.js request counts as a failed
     * add. Everything AFTER the add succeeded (pub/sub subscribers, section
     * re-render) is best-effort — a throw there previously surfaced as a bogus
     * "error while updating your cart" even though the item was in the cart.
     * @param {HTMLButtonElement} button The clicked add button.
     */
    async addVariant(button) {
      var variantId = parseInt(button.dataset.variantId, 10);
      if (!variantId || button.disabled) return;

      button.disabled = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      var cartDrawer = document.querySelector('cart-drawer');
      var sectionIds = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map((section) => section.id)
        : [];

      // No line-item properties: this bag must merge with the same bag added
      // from the PDP. Upsell attribution is a cart attribute (tagCartSource).
      var body = { items: [{ id: variantId, quantity: 1 }] };
      if (sectionIds.length) {
        body.sections = sectionIds;
        body.sections_url = window.location.pathname;
      }

      var parsed;
      try {
        var response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) throw new Error('Cart add failed (' + response.status + ')');
        parsed = await response.json();
      } catch (error) {
        // The add itself failed — restore the button and tell the shopper.
        console.error('[daddl-cart-upsells]', error);
        button.disabled = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        this.showError(this.strError);
        return;
      }

      // From here on the item IS in the cart; never show a failure to the user.
      tagCartSource('daddl_cart_upsell');

      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'daddl-cart-upsells', cartData: parsed });
        }
      } catch (error) {
        console.error('[daddl-cart-upsells] publish failed', error);
      }

      var rendered = false;
      var sectionsUsable =
        parsed &&
        parsed.sections &&
        sectionIds.length > 0 &&
        sectionIds.every(function (id) {
          return typeof parsed.sections[id] === 'string';
        });

      if (sectionsUsable && cartDrawer && typeof cartDrawer.renderContents === 'function') {
        try {
          cartDrawer.renderContents(parsed);
          rendered = true;
        } catch (error) {
          console.error('[daddl-cart-upsells] renderContents failed', error);
        }
      }

      if (!rendered) {
        // The upsell list itself is not part of the fallback re-fetch, so put
        // the button back; syncVisibility() will hide the card shortly.
        button.disabled = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        this.refreshDrawer();
      }
    }
  }

  /* ---------------------------------------------------------------------------
   * <daddl-cart-timer> — session-persistent "reserved" countdown
   * ------------------------------------------------------------------------- */

  var TIMER_STORAGE_KEY = 'daddl:cart-timer:end';

  /**
   * A cosmetic urgency countdown. The end time is persisted in sessionStorage
   * so it survives page navigation within the session, and restarts once it has
   * expired.
   */
  class DaddlCartTimer extends HTMLElement {
    connectedCallback() {
      this.output = this.querySelector('[data-daddl-timer-value]');
      this.minutes = parseInt(this.dataset.minutes, 10) || 15;

      this.endTime = this.resolveEndTime();
      this.tick = this.tick.bind(this);
      this.tick();
      this.intervalId = window.setInterval(this.tick, 1000);
    }

    disconnectedCallback() {
      if (this.intervalId) window.clearInterval(this.intervalId);
    }

    /**
     * Read a still-valid end timestamp from storage, or start a fresh window.
     * @returns {number} Epoch milliseconds when the reservation expires.
     */
    resolveEndTime() {
      var now = Date.now();
      var stored = 0;
      try {
        stored = parseInt(window.sessionStorage.getItem(TIMER_STORAGE_KEY), 10) || 0;
      } catch (error) {
        stored = 0;
      }
      if (stored > now) return stored;

      var fresh = now + this.minutes * 60 * 1000;
      try {
        window.sessionStorage.setItem(TIMER_STORAGE_KEY, String(fresh));
      } catch (error) {
        /* Storage unavailable (private mode); timer still works for the session. */
      }
      return fresh;
    }

    /** Update the mm:ss display; hide the component once the window elapses. */
    tick() {
      var remaining = Math.max(0, this.endTime - Date.now());
      var totalSeconds = Math.floor(remaining / 1000);
      var minutes = Math.floor(totalSeconds / 60);
      var seconds = totalSeconds % 60;

      if (this.output) {
        this.output.textContent =
          String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0');
      }

      if (remaining <= 0) {
        this.hidden = true;
        if (this.intervalId) window.clearInterval(this.intervalId);
        try {
          window.sessionStorage.removeItem(TIMER_STORAGE_KEY);
        } catch (error) {
          /* no-op */
        }
      }
    }
  }

  /* ---------------------------------------------------------------------------
   * Dawn CartItems patches (qty-0 remove + in-flight lock)
   * ------------------------------------------------------------------------- */

  /**
   * Patch CartItems after Dawn cart.js defines it. CartDrawerItems inherits
   * the same prototype, so drawer and /cart both get the behaviour.
   */
  function patchCartItems() {
    if (typeof CartItems === 'undefined' || CartItems.prototype.__daddlPatched) return;
    var proto = CartItems.prototype;
    proto.__daddlPatched = true;

    var originalValidate = proto.validateQuantity;
    proto.validateQuantity = function (event) {
      var inputValue = parseInt(event.target.value, 10);
      if (inputValue === 0) {
        event.target.setCustomValidity('');
        this.updateQuantity(
          event.target.dataset.index,
          0,
          event,
          document.activeElement.getAttribute('name'),
          event.target.dataset.quantityVariantId
        );
        return;
      }
      return originalValidate.call(this, event);
    };

    proto.setInteractionsDisabled = function (disabled) {
      this.querySelectorAll('.quantity__button, cart-remove-button .button').forEach(function (button) {
        button.disabled = disabled;
      });
    };

    var originalEnable = proto.enableLoading;
    proto.enableLoading = function (line) {
      originalEnable.call(this, line);
      this.setInteractionsDisabled(true);
    };

    var originalDisable = proto.disableLoading;
    proto.disableLoading = function (line) {
      originalDisable.call(this, line);
      this.setInteractionsDisabled(false);
    };
  }

  function whenCartItemsReady(attempt) {
    if (typeof CartItems !== 'undefined') {
      patchCartItems();
      return;
    }
    if (attempt > 40) return;
    window.setTimeout(function () {
      whenCartItemsReady(attempt + 1);
    }, 50);
  }

  /* ---------------------------------------------------------------------------
   * Registration
   * ------------------------------------------------------------------------- */

  /* ---------------------------------------------------------------------------
   * <daddl-cart-club-earn> — "Earn X store credit on this order"
   *
   * Liquid paints the first amount. After qty/discount changes, Dawn may
   * replace the drawer; if not, this keeps the figure on cart.total_price
   * (after discounts). Same rates as Club Flow / profile card.
   * ------------------------------------------------------------------------- */
  class DaddlCartClubEarn extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.textEl = this.querySelector('[data-daddl-club-earn-text]');
      this.rate = parseFloat(this.dataset.rate) || 0;
      this.moneyFormat = this.dataset.moneyFormat || '';
      this.template = this.dataset.template || '__AMOUNT__';

      this.unsubscribe = onCartUpdate(function (cart) {
        resolveCart(cart).then(function (resolved) {
          if (resolved) self.update(resolved);
        });
      });
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    /**
     * Repaint the estimate from the discounted cart total, matching the
     * order-paid Flow (which credits on what the shopper actually pays).
     * @param {ShopifyCart} cart
     */
    update(cart) {
      var total = typeof cart.total_price === 'number' ? cart.total_price : 0;
      var earn = Math.round((total * this.rate) / 100);

      // An empty or fully discounted cart earns nothing – say nothing.
      this.hidden = earn <= 0;
      if (this.hidden || !this.textEl) return;

      var amount = '<span class="daddl-cart-club-earn__amount">' + formatMoneyFromFormat(earn, this.moneyFormat) + '</span>';
      this.textEl.innerHTML = this.template.replace('__AMOUNT__', amount);
    }
  }

  defineElement('daddl-cart-rewards', DaddlCartRewards);
  defineElement('daddl-cart-upsells', DaddlCartUpsells);
  defineElement('daddl-cart-timer', DaddlCartTimer);
  defineElement('daddl-cart-club-earn', DaddlCartClubEarn);
  whenCartItemsReady(0);
})();
